import React, { useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_HTML = `<!-- Write HTML here -->
<div id="app"></div>`;

const DEFAULT_CSS = `/* Write CSS here */
#app { font-family: sans-serif; padding: 1rem; }`;

const DEFAULT_JS = `// Write JavaScript here
console.log("Hello from JS Try Editor!");

const app = document.getElementById("app");
if (app) {
  app.innerHTML = "<h2>It works! 🎉</h2>";
}
`;

const STORAGE_KEY = "tryjs_state_v1";

const JsEditor = () => {
  const iframeRef = useRef(null);
  const [html, setHtml] = useState(DEFAULT_HTML);
  const [css, setCss] = useState(DEFAULT_CSS);
  const [js, setJs] = useState(DEFAULT_JS);
  const [logs, setLogs] = useState([]);

  // Load saved state
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const { html, css, js } = JSON.parse(saved);
        if (html) setHtml(html);
        if (css) setCss(css);
        if (js) setJs(js);
      } catch { }
    }
  }, []);

  // Save state
  useEffect(() => {
    const id = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ html, css, js }));
    }, 200);
    return () => clearTimeout(id);
  }, [html, css, js]);

  // Listen for console messages
  useEffect(() => {
    const onMessage = (e) => {
      if (!e.data || e.data.source !== "tryjs") return;
      setLogs((prev) => [...prev, { type: e.data.type, text: e.data.args.join(" ") }]);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const srcDoc = useMemo(() => {
    return `<!doctype html><html><head><meta charset="utf-8"/>
<style>${css}</style></head><body>${html}
<script>
(function(){
  const send = (type,...args)=>parent.postMessage({source:"tryjs",type,args},"*");
  ["log","warn","error","info"].forEach(fn=>{
    const orig=console[fn];
    console[fn]=function(...args){send(fn,...args); orig.apply(console,args);}
  });
  window.onerror=(msg,src,line,col)=>send("error",msg+" ("+line+":"+col+")");
})();
<\/script>
<script>${js.replace(/<\/script/gi, "<\\/script")}<\/script>
</body></html>`;
  }, [html, css, js]);

  const run = () => {
    setLogs([]);
    if (iframeRef.current) iframeRef.current.srcdoc = srcDoc;
  };

  return (
    <div className="container">
      <h4 className="text-xl font-semibold">JS Try Editor</h4>
      <div className="row">
        <div className="col-md-4">
          <EditorCard title="HTML" value={html} onChange={setHtml} />
        </div>
        <div className="col-md-4">
          <EditorCard title="CSS" value={css} onChange={setCss} />
        </div>
        <div className="col-md-4">
          <EditorCard title="JavaScript" value={js} onChange={setJs} />
        </div>
      </div>


      <div className="row">
        <div className="col-md-6">
          <div className="p-2 border-b font-medium">Output</div>
          <iframe
            style={{ width: '100%', minHeight: '300px' }}
            ref={iframeRef}
            sandbox="allow-scripts"
            className="w-full h-64 bg-white"
            title="preview"
          />
        </div>
        <div className="col-md-6 bg-info-subtle">
          <div className="p-2 border-b font-medium flex justify-between">
            Console <button onClick={() => setLogs([])} className="text-xs">Clear</button>
          </div>
          <div className="p-2 h-40 overflow-auto font-mono text-sm">
            {logs.length === 0 && <div className="text-gray-400">No logs yet</div>}
            {logs.map((l, i) => (
              <div key={i} className={
                l.type === "error" ? "text-red-600" :
                  l.type === "warn" ? "text-yellow-600" :
                    "text-gray-800"
              }>
                [{l.type}] {l.text}
              </div>
            ))}
          </div>
        </div>
      </div>


      <button
        onClick={run}
        className="paynow_btn text-white p-2"
      >
        Run ▶
      </button>
    </div>
  );
};

const EditorCard = ({ title, value, onChange }) => (
  <div className="border rounded bg-white">
    <div className="p-2 border-b font-medium">{title}</div>
    <textarea
      style={{ width: '100%', minHeight: '300px' }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      spellCheck={false}
      className="w-full h-32 p-2 font-mono text-sm resize-y outline-none"
    />
  </div>
);

export default JsEditor;
